import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bracket } from 'react-brackets';
import { useTheme } from '@mui/material/styles';
import CustomSeed from '../CustomSeed/CustomSeed';
import { Box, Typography } from '@mui/material';
import { PrimaryTab } from 'components/Tabs';
import { get, getUserIdFromToken } from 'api';
import { TournamentPlayerCard } from 'components/Cards';
import { loserTableDates as loserTableDatesFallback } from 'constants/Tournament';
import BracketLoader from '../BracketLoader';

// Maps the bracket-round step names used by createSeeds() to the DB
// match_number column. Lets us turn a /play-dates response into a table
// shaped the same as the legacy loserTableDates constant.
const STEP_TO_MATCH_NUMBER = {
  TOP64: 'P1_rez',
  TOP32: 'P2_rez',
  TOP16: 'P3_rez',
  TOP8: 'P4_rez',
  TOP4: 'P5_rez',
  FINAL: 'P6_rez',
};

const LoserTable = ({ level, filter }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const token = getUserIdFromToken();
  const { id } = useParams();
  const [P1Data, setP1Data] = useState([]);
  const [P2Data, setP2Data] = useState([]);
  const [P3Data, setP3Data] = useState([]);
  const [P4Data, setP4Data] = useState([]);
  const [originalP1Bracket, setOriginalP1Bracket] = useState([]);
  const [originalP2Bracket, setOriginalP2Bracket] = useState([]);
  const [originalP3Bracket, setOriginalP3Bracket] = useState([]);
  const [originalP4Bracket, setOriginalP4Bracket] = useState([]);
  const [filterBracket1, setFilterBracket1] = useState(1);
  const [filterBracket2, setFilterBracket2] = useState(1);
  const [filterBracket3, setFilterBracket3] = useState(1);
  const [filterBracket4, setFilterBracket4] = useState(1);
  const [P1Bracket, setP1Bracket] = useState([]);
  const [P2Bracket, setP2Bracket] = useState([]);
  const [P3Bracket, setP3Bracket] = useState([]);
  const [P4Bracket, setP4Bracket] = useState([]);
  const [activeLevel, setActiveLevel] = useState('light');
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  // Per-(level, gender) paguoda deadlines fetched from /tournaments/play-dates.
  // Falls back to the legacy hardcoded `loserTableDatesFallback` table when the
  // backend has no data for the current level (e.g. older tournaments).
  const [loserTableDates, setLoserTableDates] = useState(loserTableDatesFallback);

  const resetFilter = () => {
    setFilterBracket1(1);
    setFilterBracket2(1);
    setFilterBracket3(1);
    setFilterBracket4(1);
  };

  const fetchMainTable = async () => {
    const response = await get(`/tournaments/tournament-paguoda/${id}`, token);
    const data = await response.json();
    const filtered = data.filter(
      (item) => item.tournament_gender === filter.gender,
    );
    setAllData(filtered);

    const levelsOrder = ['super light', 'light', 'middle', 'power', 'hard'];
    const availableLevels = filtered
      .map((item) => item.tournament_level.toLowerCase())
      .filter((level) => levelsOrder.includes(level));

    // Keep the currently selected level tab across refetches (saving a result
    // calls fetchMainTable() — unconditionally resetting here used to snap the
    // tab back to the first available level, e.g. Hard → Middle). Only pick a
    // level when the current selection isn't available in the data.
    if (!availableLevels.includes((activeLevel || '').toLowerCase())) {
      const fetchedLevel =
        availableLevels.length > 0
          ? levelsOrder.find((level) => availableLevels.includes(level))
          : 'light';

      setActiveLevel(level ? level.toLowerCase() : fetchedLevel);
    }
  };

  // Build a `loserTableDates`-shaped object from the backend response. Every
  // bracket-size key maps to the same step→date lookup, so createSeeds() calls
  // like loserTableDates['TOP8']['TOP4'] continue to work unchanged.
  const buildDateTable = (paguodaDatesForLevel) => {
    if (!paguodaDatesForLevel) return null;
    const stepDates = {};
    for (const [step, matchNumber] of Object.entries(STEP_TO_MATCH_NUMBER)) {
      if (paguodaDatesForLevel[matchNumber]) {
        stepDates[step] = paguodaDatesForLevel[matchNumber];
      }
    }
    if (Object.keys(stepDates).length === 0) return null;
    return { TOP4: stepDates, TOP8: stepDates, TOP16: stepDates, TOP32: stepDates, TOP64: stepDates };
  };

  const fetchPlayDates = async () => {
    try {
      const response = await get(`/tournaments/play-dates/${id}`, token);
      const data = await response.json();
      // Pick the right branch: level key is lower-case in DB but we may have
      // mixed casing here; do a case-insensitive lookup.
      const levelKey = Object.keys(data || {}).find(
        (k) => k.toLowerCase() === (level || activeLevel || '').toLowerCase(),
      );
      const genderBranch = levelKey ? data[levelKey]?.[filter.gender] : null;
      const built = buildDateTable(genderBranch?.Paguoda);
      if (built) setLoserTableDates(built);
    } catch (err) {
      // Fallback stays in place — silently keep using the hardcoded constants.
      console.warn('[LoserTable] play-dates fetch failed, using fallback:', err);
    }
  };

  useEffect(() => {
    fetchMainTable();
  }, [id, token, filter]);

  // Dates depend on the currently-selected level/gender too, so keep this
  // effect separate — re-running fetchMainTable on every level switch would
  // reset activeLevel back to the first available level (bug: clicking "Hard"
  // would snap back to "Middle").
  useEffect(() => {
    fetchPlayDates();
  }, [id, token, filter, level, activeLevel]);

  useEffect(() => {
    if (level) {
      setActiveLevel(level.toLowerCase());
    }
  }, [level]);

  useEffect(() => {
    const filteredGroups = allData.filter(
      (item) => item.tournament_level.toLowerCase() === activeLevel,
    );
    setFilteredData(filteredGroups);
  }, [activeLevel, allData]);

  useEffect(() => {
    const fetchData = async () => {
      const P1Data = filteredData.filter((item) => item.P1 === 1);
      const P2Data = filteredData.filter((item) => item.P2 === 1);
      const P3Data = filteredData.filter((item) => item.P3 === 1);
      const P4Data = filteredData.filter((item) => item.P4 === 1);
      setP1Data(P1Data);
      setP2Data(P2Data);
      setP3Data(P3Data);
      setP4Data(P4Data);
    };
    fetchData();
  }, [filteredData]);

  useEffect(() => {
    if (filteredData.length > 0) {
      setLoaded(true);
    }
  }, [filteredData]);

  const groupP = (property, group, dataArray) => {
    if (dataArray) {
      return dataArray
        .filter((item) => item[property + '_group'] === group)
        .map((item, index) => {
          const isChallenger = item[property + '_group'] % 2 !== 0;
          return (
            <TournamentPlayerCard
              key={index}
              pRez={item[property + '_rez']}
              first_name={item.first_name}
              last_name={item.last_name}
              winner={item[property + '_winner']}
              user_id={item.user_id}
              doubles_partner={item.doubles_partner}
              partner_first_name={item.partner_first_name}
              partner_last_name={item.partner_last_name}
              isChallenger={isChallenger}
              avatar={item.image}
              matchKey={property}
              playerGroup={item[property + '_group']}
              fullMatchKey={`${property}_${item[property + '_group']}`}
              fullRow={item}
              allPlayers={filteredData}
              fetchMainTable={fetchMainTable}
              tournament_gender={item.tournament_gender}
              tournament_level={item.tournament_level}
              tournament_stage="Paguoda"
              tournament_id={item.tournament_id}
            />
          );
        });
    } else {
      return [];
    }
  };

  const createSeeds = (
    groupFunc,
    groupNamesPairs,
    startId,
    dataArray,
    defaultName = '',
    stage,
    step,
  ) => {
    return groupNamesPairs.map(([groupName1, groupName2], index) => ({
      id: startId + index,
      date: loserTableDates[stage][step],
      isActive: allData[0]?.isActive,
      isContinued: allData[0]?.isContinued,
      stage: 'loser',
      teams: [
        {
          name:
            groupFunc(groupName1, dataArray).length > 0
              ? groupFunc(groupName1, dataArray)
              : defaultName,
        },
        {
          name:
            groupFunc(groupName2, dataArray).length > 0
              ? groupFunc(groupName2, dataArray)
              : defaultName,
        },
      ],
    }));
  };

  const createTOP64 = (dataArray) => [
    {
      title: 'TOP 64',
      seeds: createSeeds(
        (groupName) => groupP('P1', groupName, dataArray),
        [
          ['T1', 'T2'],
          ['T3', 'T4'],
          ['T5', 'T6'],
          ['T7', 'T8'],
          ['T9', 'T10'],
          ['T11', 'T12'],
          ['T13', 'T14'],
          ['T15', 'T16'],
          ['T17', 'T18'],
          ['T19', 'T20'],
          ['T21', 'T22'],
          ['T23', 'T24'],
          ['T25', 'T26'],
          ['T27', 'T28'],
          ['T29', 'T30'],
          ['T31', 'T32'],
          ['T33', 'T34'],
          ['T35', 'T36'],
          ['T37', 'T38'],
          ['T39', 'T40'],
          ['T41', 'T42'],
          ['T43', 'T44'],
          ['T45', 'T46'],
          ['T47', 'T48'],
          ['T49', 'T50'],
          ['T51', 'T52'],
          ['T53', 'T54'],
          ['T55', 'T56'],
          ['T57', 'T58'],
          ['T59', 'T60'],
          ['T61', 'T62'],
          ['T63', 'T64'],
        ],
        1,
        dataArray,
        <Box sx={{ marginLeft: '12px' }}>
          <Typography
            variant={'body4'}
            fontWeight={600}
            color={theme.palette.darkGrey[700]}
          >
            BYE..
          </Typography>
        </Box>,
        'TOP64',
        'TOP64',
      ),
    },
    {
      title: 'TOP 32',
      seeds: createSeeds(
        (groupName) => groupP('P2', groupName, dataArray),
        [
          ['S1', 'S2'],
          ['S3', 'S4'],
          ['S5', 'S6'],
          ['S7', 'S8'],
          ['S9', 'S10'],
          ['S11', 'S12'],
          ['S13', 'S14'],
          ['S15', 'S16'],
          ['S17', 'S18'],
          ['S19', 'S20'],
          ['S21', 'S22'],
          ['S23', 'S24'],
          ['S25', 'S26'],
          ['S27', 'S28'],
          ['S29', 'S30'],
          ['S31', 'S32'],
        ],
        1,
        dataArray,
        <Box sx={{ marginLeft: '12px' }}>
          <Typography
            variant={'body4'}
            fontWeight={600}
            color={theme.palette.darkGrey[700]}
          >
            BYE..
          </Typography>
        </Box>,
        'TOP64',
        'TOP32',
      ),
    },
    {
      title: 'TOP 16',
      seeds: createSeeds(
        (groupName) => groupP('P3', groupName, dataArray),
        [
          ['A1', 'A2'],
          ['A3', 'A4'],
          ['A5', 'A6'],
          ['A7', 'A8'],
          ['A9', 'A10'],
          ['A11', 'A12'],
          ['A13', 'A14'],
          ['A15', 'A16'],
        ],
        17,
        dataArray,
        '',
        'TOP64',
        'TOP16',
      ),
    },
    {
      title: 'TOP 8',
      seeds: createSeeds(
        (groupName) => groupP('P4', groupName, dataArray),
        [
          ['K1', 'K2'],
          ['K3', 'K4'],
          ['K5', 'K6'],
          ['K7', 'K8'],
        ],
        25,
        dataArray,
        '',
        'TOP64',
        'TOP8',
      ),
    },
    {
      title: t('semiFinal'),
      seeds: createSeeds(
        (groupName) => groupP('P5', groupName, dataArray),
        [
          ['PF1', 'PF2'],
          ['PF3', 'PF4'],
        ],
        29,
        dataArray,
        '',
        'TOP64',
        'TOP4',
      ),
    },
    {
      title: t('final'),
      seeds: createSeeds(
        (groupName) => groupP('P6', groupName, dataArray),
        [['F1', 'F2']],
        31,
        dataArray,
        '',
        'TOP64',
        'FINAL',
      ),
    },
    {
      title: t('thirdPlace'),
      seeds: createSeeds(
        (groupName) => groupP('P7', groupName, dataArray),
        [['F3', 'F4']],
        32,
        dataArray,
        '',
        'TOP64',
        'FINAL',
      ),
    },
  ];

  const createTOP32 = (dataArray) => [
    {
      title: 'TOP 32',
      seeds: createSeeds(
        (groupName) => groupP('P2', groupName, dataArray),
        [
          ['S1', 'S2'],
          ['S3', 'S4'],
          ['S5', 'S6'],
          ['S7', 'S8'],
          ['S9', 'S10'],
          ['S11', 'S12'],
          ['S13', 'S14'],
          ['S15', 'S16'],
          ['S17', 'S18'],
          ['S19', 'S20'],
          ['S21', 'S22'],
          ['S23', 'S24'],
          ['S25', 'S26'],
          ['S27', 'S28'],
          ['S29', 'S30'],
          ['S31', 'S32'],
        ],
        1,
        dataArray,
        <Box sx={{ marginLeft: '12px' }}>
          <Typography
            variant={'body4'}
            fontWeight={600}
            color={theme.palette.darkGrey[700]}
          >
            BYE..
          </Typography>
        </Box>,
        'TOP32',
        'TOP32',
      ),
    },
    {
      title: 'TOP 16',
      seeds: createSeeds(
        (groupName) => groupP('P3', groupName, dataArray),
        [
          ['A1', 'A2'],
          ['A3', 'A4'],
          ['A5', 'A6'],
          ['A7', 'A8'],
          ['A9', 'A10'],
          ['A11', 'A12'],
          ['A13', 'A14'],
          ['A15', 'A16'],
        ],
        17,
        dataArray,
        '',
        'TOP32',
        'TOP16',
      ),
    },
    {
      title: 'TOP 8',
      seeds: createSeeds(
        (groupName) => groupP('P4', groupName, dataArray),
        [
          ['K1', 'K2'],
          ['K3', 'K4'],
          ['K5', 'K6'],
          ['K7', 'K8'],
        ],
        25,
        dataArray,
        '',
        'TOP32',
        'TOP8',
      ),
    },
    {
      title: t('semiFinal'),
      seeds: createSeeds(
        (groupName) => groupP('P5', groupName, dataArray),
        [
          ['PF1', 'PF2'],
          ['PF3', 'PF4'],
        ],
        29,
        dataArray,
        '',
        'TOP32',
        'TOP4',
      ),
    },
    {
      title: t('final'),
      seeds: createSeeds(
        (groupName) => groupP('P6', groupName, dataArray),
        [['F1', 'F2']],
        31,
        dataArray,
        '',
        'TOP32',
        'FINAL',
      ),
    },
    {
      title: t('thirdPlace'),
      seeds: createSeeds(
        (groupName) => groupP('P7', groupName, dataArray),
        [['F3', 'F4']],
        32,
        dataArray,
        '',
        'TOP32',
        'FINAL',
      ),
    },
  ];

  const createAstuntfinalis = (dataArray) => [
    {
      title: 'TOP 16',
      seeds: createSeeds(
        (groupName) => groupP('P3', groupName, dataArray),
        [
          ['A1', 'A2'],
          ['A3', 'A4'],
          ['A5', 'A6'],
          ['A7', 'A8'],
          ['A9', 'A10'],
          ['A11', 'A12'],
          ['A13', 'A14'],
          ['A15', 'A16'],
        ],
        1,
        dataArray,
        <Box sx={{ marginLeft: '12px' }}>
          <Typography
            variant={'body4'}
            fontWeight={600}
            color={theme.palette.darkGrey[700]}
          >
            BYE..
          </Typography>
        </Box>,
        'TOP16',
        'TOP16',
      ),
    },
    {
      title: 'TOP 8',
      seeds: createSeeds(
        (groupName) => groupP('P4', groupName, dataArray),
        [
          ['K1', 'K2'],
          ['K3', 'K4'],
          ['K5', 'K6'],
          ['K7', 'K8'],
        ],
        9,
        dataArray,
        '',
        'TOP16',
        'TOP8',
      ),
    },
    {
      title: t('semiFinal'),
      seeds: createSeeds(
        (groupName) => groupP('P5', groupName, dataArray),
        [
          ['PF1', 'PF2'],
          ['PF3', 'PF4'],
        ],
        13,
        dataArray,
        '',
        'TOP16',
        'TOP4',
      ),
    },
    {
      title: t('final'),
      seeds: createSeeds(
        (groupName) => groupP('P6', groupName, dataArray),
        [['F1', 'F2']],
        15,
        dataArray,
        '',
        'TOP16',
        'FINAL',
      ),
    },
    {
      title: t('thirdPlace'),
      seeds: createSeeds(
        (groupName) => groupP('P7', groupName, dataArray),
        [['F3', 'F4']],
        16,
        dataArray,
        '',
        'TOP16',
        'FINAL',
      ),
    },
  ];

  const createKetvirtfinalis = (dataArray) => [
    {
      title: 'TOP 8',
      seeds: createSeeds(
        (groupName) => groupP('P4', groupName, dataArray),
        [
          ['K1', 'K2'],
          ['K3', 'K4'],
          ['K5', 'K6'],
          ['K7', 'K8'],
        ],
        1,
        dataArray,
        <Box sx={{ marginLeft: '12px' }}>
          <Typography
            variant={'body4'}
            fontWeight={600}
            color={theme.palette.darkGrey[700]}
          >
            BYE..
          </Typography>
        </Box>,
        'TOP8',
        'TOP8',
      ),
    },
    {
      title: t('semiFinal'),
      seeds: createSeeds(
        (groupName) => groupP('P5', groupName, dataArray),
        [
          ['PF1', 'PF2'],
          ['PF3', 'PF4'],
        ],
        9,
        dataArray,
        '',
        'TOP8',
        'TOP4',
      ),
    },
    {
      title: t('final'),
      seeds: createSeeds(
        (groupName) => groupP('P6', groupName, dataArray),
        [['F1', 'F2']],
        13,
        dataArray,
        '',
        'TOP8',
        'FINAL',
      ),
    },
    {
      title: t('thirdPlace'),
      seeds: createSeeds(
        (groupName) => groupP('P7', groupName, dataArray),
        [['F3', 'F4']],
        14,
        dataArray,
        '',
        'TOP8',
        'FINAL',
      ),
    },
  ];

  const createPusfinalis = (dataArray) => [
    {
      title: t('semiFinal'),
      seeds: createSeeds(
        (groupName) => groupP('P5', groupName, dataArray),
        [
          ['PF1', 'PF2'],
          ['PF3', 'PF4'],
        ],
        1,
        dataArray,
        '',
        'TOP4',
        'TOP4',
      ),
    },
    {
      title: t('final'),
      seeds: createSeeds(
        (groupName) => groupP('P6', groupName, dataArray),
        [['F1', 'F2']],
        5,
        dataArray,
        '',
        'TOP4',
        'FINAL',
      ),
    },
    {
      title: t('thirdPlace'),
      seeds: createSeeds(
        (groupName) => groupP('P7', groupName, dataArray),
        [['F3', 'F4']],
        6,
        dataArray,
        '',
        'TOP4',
        'FINAL',
      ),
    },
  ];

  const generateRounds = (
    filteredDataArray,
    TOP64,
    TOP32,
    astuntfinalis,
    ketvirtfinalis,
    pusfinalis,
  ) => {
    if (filteredDataArray.length <= 4) {
      return pusfinalis;
    } else if (filteredDataArray.length <= 8) {
      return ketvirtfinalis;
    } else if (filteredDataArray.length <= 16) {
      return astuntfinalis;
    } else if (filteredDataArray.length <= 32) {
      return TOP32;
    } else if (filteredDataArray.length <= 64) {
      return TOP64;
    } else {
      // Handle the case when middleLength > 32
      return null;
    }
  };

  useEffect(() => {
    const P1Bracket = generateRounds(
      P1Data,
      createTOP64(P1Data),
      createTOP32(P1Data),
      createAstuntfinalis(P1Data),
      createKetvirtfinalis(P1Data),
      createPusfinalis(P1Data),
    );
    const P2Bracket = generateRounds(
      P2Data,
      createTOP64(P2Data),
      createTOP32(P2Data),
      createAstuntfinalis(P2Data),
      createKetvirtfinalis(P2Data),
      createPusfinalis(P2Data),
    );
    const P3Bracket = generateRounds(
      P3Data,
      createTOP64(P3Data),
      createTOP32(P3Data),
      createAstuntfinalis(P3Data),
      createKetvirtfinalis(P3Data),
      createPusfinalis(P3Data),
    );
    const P4Bracket = generateRounds(
      P4Data,
      createTOP64(P4Data),
      createTOP32(P4Data),
      createAstuntfinalis(P4Data),
      createKetvirtfinalis(P4Data),
      createPusfinalis(P4Data),
    );
    setOriginalP1Bracket(P1Bracket);
    setOriginalP2Bracket(P2Bracket);
    setOriginalP3Bracket(P3Bracket);
    setOriginalP4Bracket(P4Bracket);
    setP1Bracket(P1Bracket);
    setP2Bracket(P2Bracket);
    setP3Bracket(P3Bracket);
    setP4Bracket(P4Bracket);
    // loserTableDates is read inside createSeeds() via closure, so include it
    // in deps — otherwise fetchPlayDates() would update state but the bracket
    // would keep rendering with stale (fallback) dates.
  }, [P1Data, P2Data, P3Data, P4Data, loserTableDates]);

  useEffect(() => {
    setP1Bracket(originalP1Bracket.slice(filterBracket1 - 1));
    setP2Bracket(originalP2Bracket.slice(filterBracket2 - 1));
    setP3Bracket(originalP3Bracket.slice(filterBracket3 - 1));
    setP4Bracket(originalP4Bracket.slice(filterBracket4 - 1));
  }, [
    originalP1Bracket,
    originalP2Bracket,
    originalP3Bracket,
    originalP4Bracket,
    filterBracket1,
    filterBracket2,
    filterBracket3,
    filterBracket4,
  ]);

  const handleSelectFilter = (tabName, bracket) => {
    if (bracket === 'P1') {
      const index = originalP1Bracket.findIndex(
        (round) => round.title === tabName,
      );
      setFilterBracket1(index + 1);
    }
    if (bracket === 'P2') {
      const index = originalP2Bracket.findIndex(
        (round) => round.title === tabName,
      );
      setFilterBracket2(index + 1);
    }
    if (bracket === 'P3') {
      const index = originalP3Bracket.findIndex(
        (round) => round.title === tabName,
      );
      setFilterBracket3(index + 1);
    }
    if (bracket === 'P4') {
      const index = originalP4Bracket.findIndex(
        (round) => round.title === tabName,
      );
      setFilterBracket4(index + 1);
    }
  };

  if (!loaded) {
    return <BracketLoader />;
  }

  return (
    <Box>
      <Box
        maxWidth={'700px'}
        marginBottom={'24px'}
        display={{ xs: 'flex', sm: 'inline-flex' }}
        justifyContent={'space-between'}
        border={`1px solid ${theme.palette.darkGrey[300]}`}
        borderRadius={'8px'}
        flexDirection={{ xs: 'column', sm: 'row' }}
      >
        {['super light', 'light', 'middle', 'power', 'hard'].map((level) => {
          const hasLevel = allData.some(
            (item) => item.tournament_level.toLowerCase() === level,
          );

          return (
            hasLevel && (
              <PrimaryTab
                key={level}
                activeTab={activeLevel}
                setActiveTab={setActiveLevel}
                tabName={level}
                resetFilter={resetFilter}
                pageName={'loserTable'}
              />
            )
          );
        })}
      </Box>
      {P1Data.length > 0 && (
        <>
          <Box>
            <Box
              maxWidth={'700px'}
              marginBottom={'24px'}
              display={{ xs: 'flex', sm: 'inline-flex' }}
              justifyContent={'space-between'}
              border={`1px solid ${theme.palette.darkGrey[300]}`}
              borderRadius={'8px'}
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              {originalP1Bracket.map((round, index) => {
                return (
                  <PrimaryTab
                    key={index}
                    tabName={round.title}
                    activeTab={originalP1Bracket[filterBracket1 - 1].title}
                    setActiveTab={() => handleSelectFilter(round.title, 'P1')}
                    pageName={'loserTable'}
                  />
                );
              })}
            </Box>
          </Box>
          <div style={{ marginBottom: '50px' }}>
            <Bracket
              rounds={P1Bracket}
              roundTitleComponent={(title, roundIndex) => {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderBottom: `2px solid ${theme.palette.darkGrey[700]}`,
                      padding: '12px',
                      width: '90%',
                      margin: '0 auto',
                    }}
                  >
                    <Typography
                      variant={'body3'}
                      fontWeight={600}
                      color={theme.palette.darkGrey[700]}
                    >
                      {title}
                    </Typography>
                  </Box>
                );
              }}
              renderSeedComponent={CustomSeed}
              swipeableProps={{
                disabled: true,
              }}
            />
          </div>
        </>
      )}
      {P2Data.length > 0 && (
        <>
          <Box>
            <Box
              maxWidth={'700px'}
              marginBottom={'24px'}
              display={'inline-flex'}
              justifyContent={'space-between'}
              border={`1px solid ${theme.palette.darkGrey[300]}`}
              borderRadius={'8px'}
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              {originalP2Bracket.map((round, index) => {
                return (
                  <PrimaryTab
                    key={index}
                    tabName={round.title}
                    activeTab={originalP2Bracket[filterBracket2 - 1].title}
                    setActiveTab={() => handleSelectFilter(round.title, 'P2')}
                    pageName={'loserTable'}
                  />
                );
              })}
            </Box>
          </Box>
          <div style={{ marginBottom: '50px' }}>
            <Bracket
              rounds={P2Bracket}
              roundTitleComponent={(title, roundIndex) => {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderBottom: `2px solid ${theme.palette.darkGrey[700]}`,
                      padding: '12px',
                      width: '90%',
                      margin: '0 auto',
                    }}
                  >
                    <Typography
                      variant={'body3'}
                      fontWeight={600}
                      color={theme.palette.darkGrey[700]}
                    >
                      {title}
                    </Typography>
                  </Box>
                );
              }}
              renderSeedComponent={CustomSeed}
              swipeableProps={{
                disabled: true,
              }}
            />
          </div>
        </>
      )}
      {P3Data.length > 0 && (
        <>
          {' '}
          <Box>
            <Box
              maxWidth={'700px'}
              marginBottom={'24px'}
              display={'inline-flex'}
              justifyContent={'space-between'}
              border={`1px solid ${theme.palette.darkGrey[300]}`}
              borderRadius={'8px'}
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              {originalP3Bracket.map((round, index) => {
                return (
                  <PrimaryTab
                    key={index}
                    tabName={round.title}
                    activeTab={originalP3Bracket[filterBracket3 - 1].title}
                    setActiveTab={() => handleSelectFilter(round.title, 'P3')}
                    pageName={'loserTable'}
                  />
                );
              })}
            </Box>
          </Box>
          <div style={{ marginBottom: '50px' }}>
            <Bracket
              rounds={P3Bracket}
              roundTitleComponent={(title, roundIndex) => {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderBottom: `2px solid ${theme.palette.darkGrey[700]}`,
                      padding: '12px',
                      width: '90%',
                      margin: '0 auto',
                    }}
                  >
                    <Typography
                      variant={'body3'}
                      fontWeight={600}
                      color={theme.palette.darkGrey[700]}
                    >
                      {title}
                    </Typography>
                  </Box>
                );
              }}
              renderSeedComponent={CustomSeed}
              swipeableProps={{
                disabled: true,
              }}
            />
          </div>
        </>
      )}
      {P4Data.length > 0 && (
        <>
          {' '}
          <Box>
            <Box
              maxWidth={'700px'}
              marginBottom={'24px'}
              display={'inline-flex'}
              justifyContent={'space-between'}
              border={`1px solid ${theme.palette.darkGrey[300]}`}
              borderRadius={'8px'}
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              {originalP4Bracket.map((round, index) => {
                return (
                  <PrimaryTab
                    key={index}
                    tabName={round.title}
                    activeTab={originalP4Bracket[filterBracket4 - 1].title}
                    setActiveTab={() => handleSelectFilter(round.title, 'P4')}
                    pageName={'loserTable'}
                  />
                );
              })}
            </Box>
          </Box>
          <div>
            <Bracket
              rounds={P4Bracket}
              roundTitleComponent={(title, roundIndex) => {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderBottom: `2px solid ${theme.palette.darkGrey[700]}`,
                      padding: '12px',
                      width: '90%',
                      margin: '0 auto',
                    }}
                  >
                    <Typography
                      variant={'body3'}
                      fontWeight={600}
                      color={theme.palette.darkGrey[700]}
                    >
                      {title}
                    </Typography>
                  </Box>
                );
              }}
              renderSeedComponent={CustomSeed}
              swipeableProps={{
                disabled: true,
              }}
            />
          </div>
        </>
      )}
    </Box>
  );
};

export default LoserTable;
